import React, { useMemo } from "react";
import { Menu } from "@headlessui/react";
import classNames from "classnames";
import { ChevronDownIcon } from "@/icons/primary/chevron-down-icon";

export type CaStatusDropdownOption = {
  label: string;
  value: string | number | null;
  intent?: "success" | "warning" | "danger" | "neutral";
};

export type CaStatusDropdownProps = {
  options: CaStatusDropdownOption[];
  onChange?: (option: CaStatusDropdownOption) => void;
  value?: CaStatusDropdownOption["value"];
};

const INTENT_CLASSES = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  neutral: "bg-dark-neutral-4",
};

function CaSTatusDropdown({ options, onChange, value }: CaStatusDropdownProps) {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  return (
    <Menu as='div' className='relative inline-block text-left'>
      <Menu.Button
        className={classNames(
          "flex justify-between items-center cursor-pointer gap-1.5 bg-transparent border-none text-base font-sans-serif",
        )}
      >
        <div
          className={classNames(
            "rounded-full h-1.5 w-1.5",
            INTENT_CLASSES[selectedOption?.intent || "neutral"],
          )}
        />
        <div>{selectedOption?.label || "Select an option"}</div>
        <div>
          <ChevronDownIcon className='text-mid-neutral-3' />
        </div>
      </Menu.Button>
      <Menu.Items
        className={classNames(
          "absolute left-0 mt-2 w-48 origin-top-right rounded-sm bg-white shadow-lg overflow-hidden z-50",
        )}
      >
        {options.map((option, key) => (
          <Menu.Item as='div' key={key} className=''>
            {({ active }) => (
              <button
                type='button'
                className={classNames(
                  "bg-white border-none block w-full text-left px-2.5 py-2 text-base font-sans-serif",
                  active ? "!bg-light-neutral-1" : "",
                )}
                onClick={() => onChange?.(option)}
              >
                {option.label}
              </button>
            )}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
}

CaSTatusDropdown.defaultProps = {
  options: [],
  onChange: () => {},
  value: null,
};

export default CaSTatusDropdown;
